import { Controller } from "@hotwired/stimulus"

// Alerts an admin about new online orders while the admin panel is open: polls a small JSON
// endpoint, plays a chime, shows a popup and flags the tab title.
//
// Browsers only let a page make sound after the user has interacted with it, so the sound may be
// "blocked" until the first click after a full page load. The bell button in the header shows that
// state; any click on the page unlocks it.

const CURSOR_KEY = "orderAlerts:cursor"
const SOUND_KEY = "orderAlerts:sound"
const MAX_CURSOR_AGE_MS = 12 * 60 * 60 * 1000
const MAX_TOASTS = 3

// The alert phrase: [frequency Hz, start offset s]. Rings ALERT_REPEATS times, PHRASE_LENGTH s apart.
// Kept in the 1-2 kHz range, where ears and laptop/phone speakers are most sensitive.
const PHRASE = [[1046.5, 0], [1318.5, 0.2], [1568, 0.4], [1318.5, 0.6], [1568, 0.8], [2093, 1.0]]
const PHRASE_LENGTH = 2
const ALERT_REPEATS = 4
const LOUDNESS_BOOST = 1.8 // pushed into the limiter below, which caps the peaks at full scale

// Shared across Turbo page changes (this controller reconnects on every visit).
let audioCtx = null
const seenOrderIds = new Set()

export default class extends Controller {
  static targets = ["bell", "label"]
  static values = { url: String, cursor: String, interval: { type: Number, default: 10000 } }

  connect() {
    this.cursor = this.loadCursor()
    this.unseen = 0
    this.soundEnabled = this.loadSoundPreference()
    this.polling = false

    this.unlock = () => this.resumeAudio()
    this.onVisibility = () => {
      if (document.visibilityState === "visible") {
        this.clearUnseen()
        this.poll()
      }
    }
    document.addEventListener("pointerdown", this.unlock, { passive: true })
    document.addEventListener("keydown", this.unlock, { passive: true })
    document.addEventListener("visibilitychange", this.onVisibility)

    this.ensureAudio()
    this.renderBell()
    this.poll()
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  disconnect() {
    clearInterval(this.timer)
    document.removeEventListener("pointerdown", this.unlock)
    document.removeEventListener("keydown", this.unlock)
    document.removeEventListener("visibilitychange", this.onVisibility)
    if (this.toastBox) this.toastBox.remove()
  }

  // -- bell button --

  toggleSound() {
    this.soundEnabled = !this.soundEnabled
    try { localStorage.setItem(SOUND_KEY, this.soundEnabled ? "on" : "off") } catch (_) {}
    if (this.soundEnabled) {
      this.resumeAudio()
      this.chime(1) // doubles as a test so the admin hears what to expect
    }
    this.renderBell()
  }

  renderBell() {
    if (!this.hasBellTarget) return
    const blocked = this.soundEnabled && audioCtx && audioCtx.state !== "running"
    const state = !this.soundEnabled ? "off" : blocked ? "blocked" : "on"
    const labels = { on: "Alerts on · click to mute", off: "Alerts muted · click to unmute", blocked: "Click to enable alert sound" }
    const styles = {
      on: "bg-emerald-50 text-emerald-700 border-emerald-200",
      off: "bg-gray-100 text-gray-500 border-gray-200",
      blocked: "bg-amber-50 text-amber-700 border-amber-300 animate-pulse"
    }
    this.bellTarget.className = `flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium shrink-0 ${styles[state]}`
    this.bellTarget.title = labels[state]
    this.bellTarget.setAttribute("aria-label", labels[state])
    if (this.hasLabelTarget) this.labelTarget.textContent = labels[state]
  }

  // -- polling --

  async poll() {
    if (this.polling) return
    this.polling = true
    try {
      const response = await fetch(`${this.urlValue}?since=${encodeURIComponent(this.cursor)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store"
      })
      if (!response.ok) return // signed out or server hiccup: try again next tick
      const data = await response.json()
      this.cursor = data.cursor
      try { localStorage.setItem(CURSOR_KEY, this.cursor) } catch (_) {}
      if (data.orders.length > 0) this.notify(data.orders)
    } catch (_) {
      // offline or a transient error; the next poll retries
    } finally {
      this.polling = false
    }
  }

  notify(orders) {
    const fresh = orders.filter((order) => !seenOrderIds.has(order.id))
    fresh.forEach((order) => seenOrderIds.add(order.id))
    if (fresh.length === 0 || !this.element.isConnected) return

    const soundPlayed = this.soundEnabled ? this.chime() : true
    this.showToasts(fresh, soundPlayed)

    this.unseen += fresh.length
    if (document.visibilityState !== "visible" || !document.hasFocus()) this.flagTitle()
    this.renderBell()
  }

  // -- popups --

  showToasts(orders, soundPlayed) {
    const box = this.ensureToastBox()
    orders.slice(0, MAX_TOASTS).forEach((order) => box.appendChild(this.buildToast(order)))

    if (orders.length > MAX_TOASTS) {
      box.appendChild(this.textToast(`+ ${orders.length - MAX_TOASTS} more new orders`, "/admin/orders"))
    }
    if (!soundPlayed) {
      box.appendChild(this.textToast("Sound is blocked by the browser. Click anywhere on the page to enable it.", null, true))
    }
  }

  buildToast(order) {
    const link = document.createElement("a")
    link.href = order.url
    link.className = "block w-80 bg-white border border-emerald-200 shadow-lg rounded-xl p-4 hover:bg-emerald-50 transition-colors"

    const heading = document.createElement("p")
    heading.className = "font-bold text-emerald-700"
    heading.textContent = "🔔 New online order"

    const number = document.createElement("p")
    number.className = "text-sm font-semibold text-gray-800"
    number.textContent = `${order.order_number} · ₹${Number(order.total).toLocaleString("en-IN")}`

    const details = document.createElement("p")
    details.className = "text-sm text-gray-600"
    details.textContent = `${order.customer} · ${order.items} ${order.items === 1 ? "item" : "items"} · ${order.payment_method}`

    link.append(heading, number, details)
    return this.wrapToast(link)
  }

  textToast(message, href, warning = false) {
    const el = document.createElement(href ? "a" : "div")
    if (href) el.href = href
    el.className = `block w-80 shadow-lg rounded-xl p-3 text-sm ${warning ? "bg-amber-50 border border-amber-300 text-amber-800" : "bg-white border border-gray-200 text-gray-700"}`
    el.textContent = message
    return this.wrapToast(el)
  }

  wrapToast(content) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    const close = document.createElement("button")
    close.type = "button"
    close.textContent = "×"
    close.setAttribute("aria-label", "Dismiss")
    close.className = "absolute top-1 right-2 text-gray-400 hover:text-gray-700 text-lg leading-none"
    close.addEventListener("click", () => wrapper.remove())
    wrapper.append(content, close)
    setTimeout(() => wrapper.remove(), 30000)
    return wrapper
  }

  ensureToastBox() {
    if (!this.toastBox || !this.toastBox.isConnected) {
      this.toastBox = document.createElement("div")
      this.toastBox.className = "fixed top-4 right-4 z-[100] flex flex-col gap-3"
      this.element.appendChild(this.toastBox)
    }
    return this.toastBox
  }

  // -- tab title --

  flagTitle() {
    document.title = document.title.replace(/^\(\d+\) 🔔 /, "")
    document.title = `(${this.unseen}) 🔔 ${document.title}`
    window.addEventListener("focus", () => this.clearUnseen(), { once: true })
  }

  clearUnseen() {
    this.unseen = 0
    document.title = document.title.replace(/^\(\d+\) 🔔 /, "")
  }

  // -- sound --

  ensureAudio() {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return null
      audioCtx = new Ctx()
      audioCtx.addEventListener("statechange", () => this.renderBell())
    }
    return audioCtx
  }

  resumeAudio() {
    const ctx = this.ensureAudio()
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {})
  }

  // Returns false if the browser is still blocking sound.
  // `repeats` plays the phrase that many times in a row (new orders ring ALERT_REPEATS times, the bell's test once).
  chime(repeats = ALERT_REPEATS) {
    const ctx = this.ensureAudio()
    if (!ctx) return false
    if (ctx.state === "suspended") ctx.resume().catch(() => {}) // only succeeds after a user gesture
    if (ctx.state !== "running") return false

    // Tones -> boost -> limiter -> speakers. The limiter stops the boosted signal from clipping harshly,
    // so the sound is as loud as the browser allows without crackling.
    const limiter = ctx.createDynamicsCompressor()
    limiter.threshold.value = -3
    limiter.knee.value = 0
    limiter.ratio.value = 20
    limiter.attack.value = 0.001
    limiter.release.value = 0.1
    limiter.connect(ctx.destination)
    const boost = ctx.createGain()
    boost.gain.value = LOUDNESS_BOOST
    boost.connect(limiter)

    const start = ctx.currentTime + 0.05
    for (let repeat = 0; repeat < repeats; repeat++) {
      PHRASE.forEach(([frequency, offset]) => {
        const at = start + repeat * PHRASE_LENGTH + offset
        // A square wave is full of harmonics, so it sounds much louder and more piercing than a sine at the
        // same level; the sine underneath keeps the note clear.
        this.tone(ctx, boost, "square", frequency, at, 0.45)
        this.tone(ctx, boost, "sine", frequency, at, 0.6)
      })
    }
    return true
  }

  tone(ctx, destination, type, frequency, at, peak) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.value = frequency
    gain.gain.setValueAtTime(0.0001, at)
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.01)
    gain.gain.setValueAtTime(peak, at + 0.16)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.3)
    osc.connect(gain).connect(destination)
    osc.start(at)
    osc.stop(at + 0.35)
  }

  // -- persisted state --

  // Resume from where this browser last checked (shared by every admin tab) so orders that arrive
  // between page loads are not missed; otherwise start from the server time the page rendered.
  loadCursor() {
    try {
      const stored = localStorage.getItem(CURSOR_KEY)
      const age = stored ? Date.now() - Date.parse(stored) : NaN
      if (stored && age >= 0 && age < MAX_CURSOR_AGE_MS) return stored
    } catch (_) {}
    return this.cursorValue
  }

  loadSoundPreference() {
    try { return localStorage.getItem(SOUND_KEY) !== "off" } catch (_) { return true }
  }
}
